<template>
    <v-form ref="form" v-model="valid" :disabled="isLoading" data-cy="document-form">
        <v-row dense>
            <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                    v-model="formData.documentType"
                    :items="documentTypes"
                    :label="`${$t('document_type')}*`"
                    clearable
                    density="compact"
                    variant="outlined"
                    color="primary"
                    data-cy="document-type"
                    :rules="[requiredRule('document_type')]"
                />
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <order-service-daterange
                    :start-date-label="`${$t('validity')} ${$t('from')}`"
                    :end-date-label="`${$t('validity')} ${$t('to')}`"
                    dense
                    data-cy="date-range"
                    :single-line="false"
                    :clearable="true"
                    :hide-details="false"
                    :_start-date="formData.validFrom"
                    :_end-date="formData.validTo"
                    @change-start-date="formData.validFrom = $event"
                    @change-end-date="formData.validTo = $event"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="7">
                <v-file-input
                    v-model="file"
                    clearable
                    density="compact"
                    variant="outlined"
                    color="primary"
                    data-cy="document"
                    :label="`${$t('document')}*`"
                    truncate-length="20"
                    show-size
                    :rules="[requiredRule('document'), fileSizeRule]"
                    @update:model-value="onFileLoad"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="7">
                <v-text-field
                    v-model="formData.name"
                    clearable
                    density="compact"
                    variant="outlined"
                    color="primary"
                    data-cy="name"
                    :disabled="!file"
                    :label="`${$t('name')}*`"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
        </v-row>
        <div class="font-italic text-subtitle-2 pb-4">
            {{ $t('upload_file_note') }}
        </div>
        <v-row no-gutters class="gc-4">
            <v-btn :disabled="isLoading || !valid" :loading="isLoading" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" variant="text" @click="emit('cancel')">{{ $t('cancel') }}</v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import type {SelectOptionType} from '~/utils/types/types'

    const initFormData = (): documentFormType => ({
        documentType: null,
        validFrom: null,
        validTo: null,
        name: null,
    })

    type documentFormType = {
        documentType: string
        validFrom?: string
        validTo?: string
        name: string
    }

    const props = defineProps<{
        documentId?: number | null
        documentTypes: SelectOptionType[]
        companyId: number
        attachToId: number
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const formData = ref<documentFormType>(initFormData())
    const file = ref()

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const base64 = await blobToBase64(file.value)
            const encodedData = base64.split('base64,')[1]
            const contentType = base64.split(';base64,')[0].split('data:')[1]

            const fileExtend = file.value.name.split('.')[1]
            const data = Object.assign(formData.value, {
                attachToOrganizationId: props.companyId === props.attachToId ? props.attachToId : null,
                attachToUserId: props.companyId !== props.attachToId ? props.attachToId : null,
                encodedData,
                contentType,
                name: formData.value.name.includes(fileExtend)
                    ? formData.value.name
                    : `${formData.value.name}.${fileExtend}`,
            })
            clean(data)

            if (props.documentId) {
                //TODO [p4] add put for edit document
            } else {
                await app.$api.documents.post(data)
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const onFileLoad = (): void => {
        formData.value.name = file.value ? file.value.name : null
    }

    const fileSizeRule = (v): string => {
        if (v && v[0] && v[0].size > 10 * 1024 * 1024) {
            return `${t('file_size_error')}`
        }
    }
</script>
